import React from 'react'
import {useTranslation} from 'react-i18next'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckIcon from '@mui/icons-material/Check'
import {Box} from '@mui/material'
import {
  getProductLineColors,
  PGButton,
  PGTypography,
  useProductLine,
} from '@progynyglobal/pg-ui-library'
import clsx from 'clsx'
import {makeStyles} from 'tss-react/mui'

import {theme} from '@root/theme'

export interface BookingStep {
  step: 1 | 2 | 3 | 4
  label: string
}

export interface BookingStepperProps {
  steps: BookingStep[]
  activeStep: 1 | 2 | 3 | 4
  onBack: () => void
  onBackStep: () => void
  onContinue: () => void
  continueDisabled?: boolean
  continueLoading?: boolean
  onDone?: () => void
  children?: React.ReactNode
}

const BookingStepper: React.FC<BookingStepperProps> = ({
  steps,
  activeStep,
  onBack,
  onBackStep,
  onContinue,
  continueDisabled,
  continueLoading,
  onDone,
  children,
}) => {
  const {t} = useTranslation()
  const {productLine} = useProductLine()
  const themeColors = getProductLineColors(productLine)
  const {classes} = useStyles({
    primaryLight: themeColors.primaryLight,
    primary: themeColors.primary,
  })

  return (
    <Box border='1px solid #ededed' borderRadius='16px'>
      <Box className={classes.header}>
        <PGButton
          disableRipple
          variant='text'
          startIcon={
            <ArrowBackIcon sx={{fontSize: '15px', color: '#6b7280'}} />
          }
          onClick={onBack}
          style={{padding: 0, color: '#6b7280'}}
          label={t('consultations_calendar_embed_back_to_consultations')}
        />
        <Box className={classes.stepsRow}>
          {steps.map(({step, label}, index) => {
            const isActive = step === activeStep
            const isPast = activeStep > step

            return (
              <React.Fragment key={step}>
                {index > 0 && (
                  <Box
                    width='30px'
                    height='1px'
                    bgcolor={themeColors.primaryLight}
                  />
                )}
                <Box display='flex' alignItems='center' gap='8px'>
                  <Box
                    className={clsx(classes.stepCircle, {
                      [classes.stepCircleActive]: isActive,
                      [classes.stepCirclePast]: isPast,
                    })}
                  >
                    {isPast ? (
                      <CheckIcon sx={{color: '#1E393B', height: '16px'}} />
                    ) : (
                      <PGTypography
                        variant='subheader'
                        as='span'
                        color={
                          isActive
                            ? theme.palette.other.brandColors.white
                            : '#6b7280'
                        }
                        style={{fontSize: 12}}
                      >
                        {step}
                      </PGTypography>
                    )}
                  </Box>
                  <PGTypography
                    variant={isActive ? 'subheader' : 'caption'}
                    as='span'
                    color={isActive ? '' : '#6b7280'}
                  >
                    {label}
                  </PGTypography>
                </Box>
              </React.Fragment>
            )
          })}
        </Box>
      </Box>

      {children}

      {activeStep !== 4 && (
        <Box className={classes.footer}>
          <Box>
            {activeStep !== 1 && (
              <PGButton
                label={t('common_back')}
                startIcon={<ArrowBackIcon sx={{fontSize: '15px'}} />}
                onClick={onBackStep}
                variant='outlined'
              />
            )}
          </Box>
          <PGButton
            label={
              activeStep !== 3
                ? t('common_continue')
                : t('consultations_calendar_embed_language_step_book')
            }
            endIcon={<ArrowForwardIcon sx={{fontSize: '20px'}} />}
            onClick={onContinue}
            disabled={continueDisabled || continueLoading}
            loading={continueLoading}
          />
        </Box>
      )}

      {activeStep === 4 && onDone && (
        <Box className={clsx(classes.footer, classes.footerEnd)}>
          <PGButton
            label={t('consultations_calendar_embed_step_done')}
            onClick={onDone}
          />
        </Box>
      )}
    </Box>
  )
}

const useStyles = makeStyles<{primaryLight: string; primary: string}>()(
  (theme, {primaryLight, primary}) => ({
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      paddingLeft: '24px',
      paddingRight: '24px',
      paddingTop: '18px',
      paddingBottom: '18px',
      borderBottom: '1px solid #ededed',
    },
    stepsRow: {
      flex: '1 0 0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
    },
    stepCircle: {
      width: '26px',
      height: '26px',
      borderRadius: '99px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#ededed',
    },
    stepCircleActive: {
      backgroundColor: primary,
    },
    stepCirclePast: {
      backgroundColor: '#E3FAE3',
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: '24px',
      paddingRight: '24px',
      paddingTop: '14px',
      paddingBottom: '14px',
      borderTop: `1px solid ${primaryLight}`,
    },
    footerEnd: {
      justifyContent: 'flex-end',
    },
  })
)

export default BookingStepper
